import React from 'react';
import styled from 'styled-components';
import { useFormContext, UseFormGetValues } from 'react-hook-form';
import { PresetCreateRequest, PresetUpdateRequest, PresetType, TradeSetCreateRequest, OrderType, TradeSetPresetLink } from '../../../types/preset';

// Helper function to check if order type is a cancellation type
const isCancellationOrder = (orderType?: OrderType): boolean => {
  return !!orderType && ['cancel_all', 'cancel_bids', 'cancel_asks'].includes(orderType);
};

// Define props without getFormData
interface ReviewConfirmProps {
  presetType: PresetType;
}

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #4b5563;
  margin: 0 0 0.5rem 0;
`;

const SummaryCard = styled.div`
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1.5rem;
`;

const SummarySection = styled.div`
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SummarySectionTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  color: #4b5563;
  margin: 0 0 0.75rem 0;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.5rem;
`;

const SummaryRow = styled.div`
  display: flex;
  margin-bottom: 0.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SummaryLabel = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  width: 40%;
  padding-right: 1rem;
`;

const SummaryValue = styled.div`
  font-size: 0.875rem;
  color: #1f2937;
  font-weight: 500;
  flex: 1;
`;

const BadgeContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Badge = styled.span<{ type?: string }>`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  
  background-color: ${({ type }) => {
    switch (type) {
      case 'success':
        return '#d1fae5';
      case 'warning':
        return '#fef3c7';
      case 'danger':
        return '#fee2e2';
      default:
        return '#e5e7eb';
    }
  }};
  
  color: ${({ type }) => {
    switch (type) {
      case 'success':
        return '#065f46';
      case 'warning':
        return '#92400e';
      case 'danger':
        return '#b91c1c';
      default:
        return '#4b5563';
    }
  }};
`;

const Value = styled.span`
  font-weight: normal;
  margin-left: 5px;
  word-break: break-word;
`;

const Section = styled.div`
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
  &:last-of-type {
    border-bottom: none;
  }
`;

const Row = styled.div`
  display: flex;
  margin-bottom: 0.5rem;
  line-height: 1.4;
`;

const Label = styled.div`
  font-size: 0.9em;
  color: #555;
  width: 150px;
  flex-shrink: 0;
  padding-right: 10px;
`;

// NEW: TradeSet Preset List Styles
const PresetLinksList = styled.div`
  margin-top: 0.5rem;
`;

const PresetLinkItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  margin-bottom: 0.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const OrderBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background-color: #3b82f6;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 50%;
  margin-right: 0.75rem;
  flex-shrink: 0;
`;

const PresetInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const PresetName = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
`;

const PresetDetails = styled.span`
  font-size: 0.75rem;
  color: #6b7280;
`;

const ReviewConfirm: React.FC<ReviewConfirmProps> = ({ presetType }) => {
  // Get getValues from context now that FormProvider is correctly typed
  const { getValues } = useFormContext<PresetCreateRequest | TradeSetCreateRequest>(); 
  const formData = getValues();

  const displayValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined || value === '') return <Value>Not set</Value>;
    if (typeof value === 'boolean') return <Value>{value ? 'Yes' : 'No'}</Value>;
    return <Value>{String(value)}</Value>;
  };

  const displayNestedValue = (obj: any, key: string): React.ReactNode => {
     return displayValue(obj?.[key]);
  };
  
  const displayPriceWithType = (priceValue: any, priceType: any): React.ReactNode => {
     const typeString = priceType && priceType !== 'absolute' ? ` (${priceType})` : '';
     const valueString = (priceValue === null || priceValue === undefined || priceValue === '') ? 'Not set' : String(priceValue);
     return <Value>{valueString}{typeString}</Value>;
  }

  return (
    <StepContainer>
      <FormSection>
        <SectionTitle>Review & Confirm</SectionTitle>
        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '-0.5rem' }}>
            Please review all the settings below before submitting your {presetType}.
        </p>
        
        <SummaryCard>
          <Section>
            <SectionTitle>Basic Information</SectionTitle>
            <Row><Label>Name:</Label> {displayValue(formData.name)}</Row>
            <Row><Label>Symbol:</Label> {displayValue(formData.symbol)}</Row>
            {/* Description might be optional or only for TradeSet? Adjust as needed */}
            {formData.description && <Row><Label>Description:</Label> {displayValue(formData.description)}</Row>}
          </Section>

          {/* TradeSet specific fields */}
          {presetType === 'tradeset' && 'presets' in formData && (
            <Section>
              <SectionTitle>Linked Presets</SectionTitle>
              {!formData.presets || formData.presets.length === 0 ? (
                <Row>
                  <Label>Status:</Label>
                  <Value style={{ fontStyle: 'italic', color: '#6b7280' }}>
                    No presets linked. You can add presets later using the Edit TradeSet function.
                  </Value>
                </Row>
              ) : (
                <>
                  <Row>
                    <Label>Total Presets:</Label>
                    <Value>{formData.presets.length}</Value>
                  </Row>
                  <PresetLinksList>
                    {formData.presets
                      .sort((a, b) => a.order - b.order)
                      .map((presetLink: TradeSetPresetLink, index) => (
                        <PresetLinkItem key={presetLink.preset_id}>
                          <OrderBadge>{presetLink.order}</OrderBadge>
                          <PresetInfo>
                            <PresetName>Preset ID: {presetLink.preset_id}</PresetName>
                            <PresetDetails>Will execute in position #{presetLink.order}</PresetDetails>
                          </PresetInfo>
                        </PresetLinkItem>
                      ))}
                  </PresetLinksList>
                </>
              )}
            </Section>
          )}

          {/* Conditional rendering for Preset specific fields */}
          {presetType === 'preset' && 'order_type' in formData && (
            <React.Fragment key="preset-details">
              <Section>
                <SectionTitle>Order Details</SectionTitle>
                <Row><Label>Order Type:</Label> {displayValue(formData.order_type)}</Row>
                <Row><Label>Side:</Label> {displayValue(formData.side)}</Row>
                <Row><Label>Amount:</Label> <Value>{displayValue(formData.amount)} ({displayValue(formData.amount_type)})</Value></Row>
                {(formData.order_type === 'limit' || formData.order_type === 'stop_limit') && (
                  <Row><Label>Limit Price:</Label> {displayPriceWithType(formData.limit_price, formData.limit_price_type)}</Row>
                )}
                {(formData.order_type === 'stop_market' || formData.order_type === 'stop_limit') && (
                  <Row><Label>Stop Price:</Label> {displayValue(formData.stop_price)}</Row>
                )}
                <Row><Label>Leverage:</Label> <Value>{displayValue(formData.leverage)}x ({displayValue(formData.margin_mode)})</Value></Row>
                <Row><Label>Flags:</Label> <Value>{formData.flags?.reduce_only ? 'Reduce-Only ' : ''}{formData.flags?.post_only ? 'Post-Only' : ''}</Value></Row>
              </Section>

              {/* Explicitly return null if condition is false */} 
              { formData.take_profit || formData.stop_loss ? (
                 <Section>
                   <SectionTitle>Risk Management</SectionTitle>
                   {/* Return null if not enabled */}
                   {formData.take_profit?.enabled ? (
                       <Row><Label>Take Profit:</Label> {displayPriceWithType(formData.take_profit.trigger_price_value, formData.take_profit.price_type)}</Row>
                   ) : null}
                   {/* Return null if not enabled */}
                   {formData.stop_loss?.enabled ? (
                       <Row><Label>Stop Loss:</Label> {displayPriceWithType(formData.stop_loss.trigger_price_value, formData.stop_loss.price_type)}</Row>
                   ) : null}
                 </Section>
              ) : null}
              
              {/* Explicitly return null if condition is false */} 
              {/* Check for layered settings existence */} 
              { formData.layered && (
                <Section>
                  <SectionTitle>Layered Order Settings</SectionTitle>
                  <Row><Label>Number of Layers:</Label> {displayNestedValue(formData.layered, 'parts')}</Row>
                  <Row><Label>Step:</Label> {displayNestedValue(formData.layered, 'step')}</Row>
                </Section>
              )}
            </React.Fragment>
          )}
          
          {/* Conditional rendering for Webhook specific fields */}
          {/* Check if webhook_id exists, implies webhook might be configured */} 
          {'webhook_id' in formData && (
            <Section>
              <SectionTitle>Webhook Configuration</SectionTitle>
              <Row><Label>Webhook Enabled:</Label> {displayValue(formData.webhook_enabled ?? false)}</Row>
              {formData.webhook_enabled && (
                <React.Fragment key="webhook-details">
                  <Row><Label>Webhook ID:</Label> {displayValue(formData.webhook_id)}</Row>
                  <Row><Label>Webhook Secret:</Label> <Value>{formData.webhook_secret ? 'Set' : 'Not Set'}</Value></Row>
                </React.Fragment>
              )}
            </Section>
          )}
        </SummaryCard>
      </FormSection>
    </StepContainer>
  );
};

export default ReviewConfirm; 